<template>
  <div>
    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{locales && locales.properties && locales.properties.dependant_leave_area }}
      </h2>
      <div class='grid grid-cols-1 lg:grid-cols-3 gap-x-5 gap-y-5 lg:gap-y-8 items-end'>
        <div>
          <theme-numeric
            v-model="leaveArea['own_room'].surface"
            name='leaveArea[own_room][surface]'
            :label='locales && locales.properties && locales.properties.own_room_kitchen_surface'
            @input='countDependantLeaveAreaPoints(leaveArea)'
            :IsExtraInfo='true'
          >
            <template slot='extra-info'> m<sup>2</sup></template>
          </theme-numeric>
        </div>
        <div>
          <theme-numeric
            v-model="leaveArea['common_area'].surface"
            name='leaveArea[common_area][surface]'
            :label='locales && locales.properties && locales.properties.common_area_with_central_heating'
            @input='countDependantLeaveAreaPoints(leaveArea)'
            :IsExtraInfo='true'
          >
            <template slot='extra-info'> m<sup>2</sup></template>
          </theme-numeric>
        </div>
        <div>
          <theme-numeric
            v-model="leaveArea['number_of_residents']"
            name='leaveArea[number_of_residents]'
            :label='locales && locales.properties && locales.properties.number_of_residents'
            @input='countDependantLeaveAreaPoints(leaveArea)'>
          </theme-numeric>
        </div>
        <div>
          <theme-numeric
            v-model='leaveArea.total'
            name='leaveArea[total]'
            :label='totalLabel(locales && locales.properties && locales.properties.leave_area)'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{ locales && locales.properties && locales.properties.heated_private_rooms }}
      </h2>
      <div class='mb-10'>
        <theme-radios
          :columnDirection='true'
          @input='countHeatingPoints'
          label=''
          name='heating[type]'
          v-model='heating.type.value'
          :options="{
              '0': locales && locales.properties && locales.properties.no_heating_or_electric,
              '1': locales && locales.properties && locales.properties.gas_heater,
              '2': locales && locales.properties && locales.properties.central_heating,
            }">
        </theme-radios>
      </div>
      <div v-if='heating.type.value == 2' class='mb-10'>
        <div class='grid grid-cols-1 lg:grid-cols-12 mb-5'>
          <div class='col-span-4'>
            <theme-numeric
              @input='countHeatingPoints'
              v-model='heating.central_heating.value'
              name='heating[central_heating]'
              :label='locales && locales.properties && locales.properties.central_heating_surface'>
            </theme-numeric>
          </div>
        </div>
        <div class='mb-5'>
          <input type='hidden' name='heating[thermostat]' value='0'/>
          <theme-checkboxes
            id='heatingThermostat'
            @input='countHeatingPoints'
            label=''
            :is-single-checkbox='true'
            name='heating[thermostat]'
            v-model='heating.thermostat.value'
            :options="{ '1': `${locales && locales.properties && locales.properties.thermostat_buttons} ${heating.thermostat.total || ''}` }">
          </theme-checkboxes>
        </div>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='heating.total'
            name='heating[total]'
            :label='totalLabel(locales && locales.properties && locales.properties.heated_private_rooms)'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{locales && locales.properties && locales.properties.kitchen_facilities_sanitary }}
      </h2>
      <div class='grid grid-cols-1 lg:grid-cols-2 gap-x-5 gap-y-5 lg:gap-y-8 mb-10'>
        <div>
          <h3 class='calculate-price__form-subtitle'>
            {{ locales && locales.properties && locales.properties.kitchen }}
            <strong v-if='kitchenFacilities.kitchen.value'>{{ kitchenFacilities.kitchen.total || '' }}</strong>
          </h3>
          <theme-radios
            :columnDirection='true'
            v-model='kitchenFacilities.kitchen.value'
            name='kitchen_facilities[kitchen]'
            :options='kitchenOptions'
            @input='countKitchenFacilitiesPoints'>
          </theme-radios>
        </div>
        <div>
          <h3 class='calculate-price__form-subtitle'>
            {{ locales && locales.properties && locales.properties.wc }}
            <strong v-if='kitchenFacilities.wc.value'>{{ kitchenFacilities.wc.total || '' }}</strong>
          </h3>
          <theme-radios
            :columnDirection='true'
            v-model='kitchenFacilities.wc.value'
            name='kitchen_facilities[wc]'
            :options='wcOptions'
            @input='countKitchenFacilitiesPoints'>
          </theme-radios>
        </div>
        <div>
          <h3 class='calculate-price__form-subtitle'>
            {{ locales && locales.properties && locales.properties.bath_shower }}
            <strong v-if='kitchenFacilities.bath_shower.value'>{{ kitchenFacilities.bath_shower.total || '' }}</strong>
          </h3>
          <theme-radios
            :columnDirection='true'
            v-model='kitchenFacilities.bath_shower.value'
            name='kitchen_facilities[bath_shower]'
            :options='bathShowerOptions'
            @input='countKitchenFacilitiesPoints'>
          </theme-radios>
        </div>
        <div>
          <h3 class='calculate-price__form-subtitle'>
            {{ locales && locales.properties && locales.properties.washbasin }}
            <strong v-if='kitchenFacilities.washbasin.value'>{{ kitchenFacilities.washbasin.total || '' }}</strong>
          </h3>
          <theme-radios
            :columnDirection='true'
            v-model='kitchenFacilities.washbasin.value'
            name='kitchen_facilities[washbasin]'
            :options='washbasinOptions'
            @input='countKitchenFacilitiesPoints'>
          </theme-radios>
        </div>
      </div>

      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='kitchenFacilities.total'
            name='kitchen_facilities[total]'
            :label='totalLabel(locales && locales.properties && locales.properties.kitchen_facilities_sanitary)'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{ locales && locales.properties && locales.properties.extra_points }}
      </h2>
      <div class='grid grid-cols-1 lg:grid-cols-12 gap-x-5 gap-y-5 lg:gap-y-8 mb-10'>
        <div class='col-span-6'>
          <h3 class='calculate-price__form-subtitle'>
            {{ locales && locales.properties && locales.properties.outdoor_space }}
            <strong v-if='extraPoints.outdoor_space.value'>{{ extraPoints.outdoor_space.total || '' }}</strong>
          </h3>
          <theme-radios
            :columnDirection='true'
            v-model='extraPoints.outdoor_space.value'
            name='extra_points[outdoor_space]'
            :options='outdoorSpaceOptions'
            @input='countExtraPoints'>
          </theme-radios>
        </div>
        <div class='col-span-6'>
          <h3 class='calculate-price__form-subtitle'>
            {{ locales && locales.properties && locales.properties.bicycle_shed }}
            <strong v-if='extraPoints.bicycle_shed.value'>{{ extraPoints.bicycle_shed.total || '' }}</strong>
          </h3>
          <theme-radios
            :columnDirection='true'
            v-model='extraPoints.bicycle_shed.value'
            name='extra_points[bicycle_shed]'
            :options='bicycleShedOptions'
            @input='countExtraPoints'>
          </theme-radios>
        </div>
      </div>
      <div class='mb-10'>
        <input type='hidden' name='extra_points[national_monument]' value='0'/>
        <theme-checkboxes
          id='extraPointsNationalMonument'
          @input='countExtraPoints'
          label=''
          :is-single-checkbox='true'
          name='extra_points[national_monument]'
          v-model='extraPoints.national_monument.value'
          :options="{ '1': `${locales && locales.properties && locales.properties.is_national_monument} ${extraPoints.national_monument.total || ''}`}">
        </theme-checkboxes>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='extraPoints.total'
            name='extra_points[total]'
            :label='totalLabel(locales && locales.properties && locales.properties.extra_points)'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{ locales && locales.properties && locales.properties.deduction_points }}
      </h2>
      <div class='mb-10' v-for='(deduction, index) in deductionOptions'>
        <div class='mb-7' v-if="index != 'total'" :key='index'>
          <input type='hidden' :name='`deduction_points[` + index + `]`' value='0'/>
          <theme-checkboxes
            :id='`deductionOptions`+index'
            @input='countDeductionPoints'
            label=''
            :is-single-checkbox='true'
            :name='`deduction_points[` + index + `]`'
            v-model='deductionOptions[index].value'
            :options="{ '1': `${locales && locales.properties && locales.properties[index]} ${deductionOptions[index].total || ''}` }">
          </theme-checkboxes>
        </div>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='deductionOptions.total'
            name='deduction_points[total]'
            :label='totalLabel(locales && locales.properties && locales.properties.deduction_points_total)'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-bottom'>
      <div class='default-content grid grid-cols-1 lg:grid-cols-12 items-center gap-1 lg:gap-5'>
        <div class='col-span-2 hidden lg:block'>
          <h3 class='calculate-price__form-bottom-title'>
            {{ this.locales && this.locales.common && this.locales.common.total }}
          </h3>
        </div>
        <div class='lg:col-span-7'>
          <div class='grid grid-cols-2 items-center gap-5'>
            <div class='grid lg:grid-cols-2 items-center gap-1 lg:gap-5 lg:pr-6'>
              <div class='calculate-price__form-bottom-label'>{{ locales && locales.properties && locales.properties.total_points }}</div>
              <div class='lg:hidden'>{{totalPoints || '0'}}</div>
              <div class='hidden lg:block'>
                <theme-numeric
                  v-model='totalPoints'
                  name='total'
                  label=''
                  :isReadonly='true'>
                </theme-numeric>
              </div>
            </div>
            <div class='grid lg:grid-cols-2 items-center gap-1 lg:gap-5 lg:pr-6'>
              <div class='calculate-price__form-bottom-label'>{{locales && locales.properties && locales.properties.max_basic_rent_price }}</div>
              <div class='lg:hidden'>{{price || '0'}}</div>
              <div class='hidden lg:block'>
                <theme-numeric
                  v-model='price'
                  name='price'
                  label=''
                  :isReadonly='true'>
                </theme-numeric>
              </div>
            </div>
          </div>
        </div>
        <div class='lg:col-span-3 grid grid-cols-2 items-center gap-5'>
          <button type='submit' class='theme-btn theme-btn--theme'>
            {{ locales && locales.common && locales.common.download_pdf }}
          </button>
          <button type='submit' name='submit' value='save' class='theme-btn theme-btn--theme'>
            {{ locales && locales.common && locales.common.save }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import {mapGetters, mapActions} from 'vuex';
  import _ from 'lodash';
  import $ from 'jquery';

  export default {
    computed: {
      ...mapGetters(['metadata', 'locales']),
    },
    data() {
      return {
        total: 0,
        totalPoints: 0,
        surface: 0,
        price: 0,
      };
    },
    props: {
      property: {
        default: () => {
          return {};
        },
        type: Object,
      },
      kitchenFacilities: {
        default: () => {
          return {};
        },
        type: Object,
      },
      extraPoints: {
        default: () => {
          return {};
        },
        type: Object,
      },
      leaveArea: {
        default: () => {
          return {};
        },
        type: Object,
      },
      heating: {
        default: () => {
          return {};
        },
        type: Object,
      },
      dependantMaxRentalPrices: {
        default: () => {
          return {};
        },
        type: [Array, Object],
      },
      kitchenOptions: {
        default: () => {
          return {};
        },
        type: Object,
      },
      wcOptions: {
        default: () => {
          return {};
        },
        type: Object,
      },
      bathShowerOptions: {
        default: () => {
          return {};
        },
        type: Object,
      },
      washbasinOptions: {
        default: () => {
          return {};
        },
        type: Object,
      },
      outdoorSpaceOptions: {
        default: () => {
          return {};
        },
        type: Object,
      },
      bicycleShedOptions: {
        default: () => {
          return {};
        },
        type: Object,
      },
      deductionOptions: {
        default: () => {
          return {};
        },
        type: Object,
      },
      dependantSettings: {
        default: () => {
          return {};
        },
        type: [Array, Object],
      },
    },
    beforeMount() {
      this.countDependantLeaveAreaPoints(this.leaveArea);
      this.countHeatingPoints();
      this.countKitchenFacilitiesPoints();
      this.countExtraPoints();
      this.countDeductionPoints();
    },
    methods: {
      countDependantLeaveAreaPoints(object) {
        var sum = 0;
        var commonAreaPoints = 0;
        var commonAreaSurface = 0;
        if (object["own_room"].surface) {
          sum +=
            Math.round(
              parseFloat((object["own_room"].surface + "").replace(",", "."))
            ) * parseFloat((this.dependantSettings.price_points_settings.dependant_leave_area.own_room_points_per_m2 + "").replace(",", "."));
        }
        if (
          object["common_area"].surface &&
          object["common_area"].surface >= 15
        ) {
          commonAreaSurface += Math.round(
            parseFloat((object["common_area"].surface + "").replace(",", "."))
          );
        }
  
        if (object["number_of_residents"] && object["number_of_residents"] >= 1) {
          commonAreaPoints =
            Math.round(
              commonAreaSurface /
                parseFloat((object["number_of_residents"] + "").replace(",", "."))
            ) * parseFloat((this.dependantSettings.price_points_settings.dependant_leave_area.common_area_points_per_m2 + "").replace(",", "."));
          sum += commonAreaPoints;
        }
        var total = sum;
        Vue.set(object, "total", total);
        this.countDependantTotal();
      },
      countHeatingPoints() {
        var sum = 0;
        var heatingPoints = 0;
        Vue.set(this.heating.type, "total", heatingPoints);
        if (this.heating.type.value == 1) {
          heatingPoints = parseFloat((this.dependantSettings.price_points_settings.dependant_heating.gas_heater + "").replace(",", "."));
          Vue.set(this.heating.type, "total", heatingPoints);
          sum += heatingPoints;
        } else if (this.heating.type.value == 2) {
          heatingPoints =
            parseFloat(this.heating.central_heating.value + "") *
            parseFloat(this.heating.central_heating.points + "");
          sum += heatingPoints;
          Vue.set(this.heating.central_heating, "total", heatingPoints);
          if (
            this.heating.thermostat.value &&
            this.heating.thermostat.value == 1
          ) {
            sum += parseFloat(this.heating.thermostat.points + "");
            Vue.set(
              this.heating.thermostat,
              "total",
              parseFloat(this.heating.thermostat.points + "")
            );
          } else {
            Vue.set(this.heating.thermostat, "total", 0);
          }
        }
        var total = sum;
        Vue.set(this.heating, "total", total);
        this.countDependantTotal();
      },
      countKitchenFacilitiesPoints() {
        var sum = 0;
  
        switch (this.kitchenFacilities.kitchen.value) {
          case "1":
            Vue.set(this.kitchenFacilities.kitchen, "total", 0);
            sum += 0;
            break;
          case "2":
            var points = parseFloat((this.dependantSettings.price_points_settings.kitchen.kitchen_for_max_5_rooms + "").replace(",", "."));
            Vue.set(this.kitchenFacilities.kitchen, "total", points);
            sum += points;
            break;
          case "3":
            var points = parseFloat((this.dependantSettings.price_points_settings.kitchen.own_kitchen_in_common_room + "").replace(",", "."));
            Vue.set(this.kitchenFacilities.kitchen, "total", points);
            sum += points;
            break;
          case "4":
            var points = parseFloat((this.dependantSettings.price_points_settings.kitchen.own_kitchen_in_living_room_15_25 + "").replace(",", "."));
            Vue.set(this.kitchenFacilities.kitchen, "total", points);
            sum += points;
            break;
          case "5":
            var points = parseFloat((this.dependantSettings.price_points_settings.kitchen.own_kitchen_in_living_room_above_25 + "").replace(",", "."));
            Vue.set(this.kitchenFacilities.kitchen, "total", points);
            sum += points;
            break;
          case "6":
            var points = parseFloat((this.dependantSettings.price_points_settings.kitchen.separate_private_kitchen + "").replace(",", "."));
            Vue.set(this.kitchenFacilities.kitchen, "total", points);
            sum += points;
            break;
          default:
            Vue.set(this.kitchenFacilities.kitchen, "total", 0);
        }
        switch (this.kitchenFacilities.wc.value) {
          case "1":         
            Vue.set(this.kitchenFacilities.wc, "total", 0);
            sum += 0;
            break;
          case "2":
            var points = parseFloat((this.dependantSettings.price_points_settings.sanitary.communal_toilet_for_max_5_rooms + "").replace(",", "."));
            Vue.set(this.kitchenFacilities.wc, "total", points);
            sum += points;
            break;
          case "3":
            var points = parseFloat((this.dependantSettings.price_points_settings.sanitary.own_toilet + "").replace(",", "."));
            Vue.set(this.kitchenFacilities.wc, "total", points);
            sum += points;
            break;
          default:
            Vue.set(this.kitchenFacilities.wc, "total", 0);
        }
        switch (this.kitchenFacilities.bath_shower.value) {
          case "1":
            Vue.set(this.kitchenFacilities.bath_shower, "total", 0);
            sum += 0;
            break;
          case "2":
            var points = parseFloat((this.dependantSettings.price_points_settings.sanitary.communal_shower_bath_for_max_8_rooms + "").replace(",", "."));
            Vue.set(this.kitchenFacilities.bath_shower, "total", points);
            sum += points;
            break;
          case "3":
            var points = parseFloat((this.dependantSettings.price_points_settings.sanitary.private_shower_bath + "").replace(",", "."));
            Vue.set(this.kitchenFacilities.bath_shower, "total", points);
            sum += points;
            break;
          default:
            Vue.set(this.kitchenFacilities.bath_shower, "total", 0);
        }
        switch (this.kitchenFacilities.washbasin.value) {
          case "1":
            Vue.set(this.kitchenFacilities.washbasin, "total", 0);
            sum += 0;
            break;
          case "2":
            var points = parseFloat((this.dependantSettings.price_points_settings.sanitary.communal_washbasin_for_max_5_rooms + "").replace(",", "."));
            Vue.set(this.kitchenFacilities.washbasin, "total", points);
            sum += points;
            break;
          case "3":
            var points = parseFloat((this.dependantSettings.price_points_settings.sanitary.own_sink + "").replace(",", "."));
            Vue.set(this.kitchenFacilities.washbasin, "total", points);
            sum += points;
            break;
          default:
            Vue.set(this.kitchenFacilities.washbasin, "total", 0);
        }
        var total = sum;
        Vue.set(this.kitchenFacilities, "total", total);
        this.countDependantTotal();
      },
      countExtraPoints() {
        var sum = 0;
  
        switch (this.extraPoints.outdoor_space.value) {
          case "1":
            Vue.set(this.extraPoints.outdoor_space, "total", 0);
            sum += 0;
            break;
          case "2":
            var points = parseFloat((this.dependantSettings.price_points_settings.outdoor.communal_outdoor_space_below_4 + "").replace(",", "."));
            Vue.set(this.extraPoints.outdoor_space, "total", points);
            sum += points;
            break;
          case "5":
            var points = parseFloat((this.dependantSettings.price_points_settings.outdoor.private_outdoor_space_below_4 + "").replace(",", "."));
            Vue.set(this.extraPoints.outdoor_space, "total", points);
            sum += points;
            break;
          case "3":
            var points = parseFloat((this.dependantSettings.price_points_settings.outdoor.communal_outdoor_space_4_10 + "").replace(",", "."));
            Vue.set(this.extraPoints.outdoor_space, "total", points);
            sum += points;
            break;
          case "4":
            var points = parseFloat((this.dependantSettings.price_points_settings.outdoor.communal_outdoor_space_above_10 + "").replace(",", "."));
            Vue.set(this.extraPoints.outdoor_space, "total", points);
            sum += points;
            break;
          case "6":
            var points = parseFloat((this.dependantSettings.price_points_settings.outdoor.private_outdoor_space_4_10 + "").replace(",", "."));
            Vue.set(this.extraPoints.outdoor_space, "total", points);
            sum += points;
            break;
          case "7":
            var points = parseFloat((this.dependantSettings.price_points_settings.outdoor.private_outdoor_space_above_10 + "").replace(",", "."));
            Vue.set(this.extraPoints.outdoor_space, "total", points);
            sum += points;
            break;
          default:
            Vue.set(this.extraPoints.outdoor_space, "total", 0);
        }
        switch (this.extraPoints.bicycle_shed.value) {
          case "1":
            Vue.set(this.extraPoints.bicycle_shed, "total", 0);
            sum += 0;
            break;
          case "2":
            var points = parseFloat((this.dependantSettings.price_points_settings.outdoor.communal_bicycle_shed + "").replace(",", "."));
            Vue.set(this.extraPoints.bicycle_shed, "total", points);
            sum += points;
            break;
          case "3":
            var points = parseFloat((this.dependantSettings.price_points_settings.outdoor.private_bicycle_shed + "").replace(",", "."));
            Vue.set(this.extraPoints.bicycle_shed, "total", points);
            sum += points;
            break;
          default:
            Vue.set(this.extraPoints.bicycle_shed, "total", 0);
        }
        if (
          this.extraPoints.national_monument.value &&
          this.extraPoints.national_monument.value == 1
        ) {
          sum += parseFloat(this.extraPoints.national_monument.points + "");
          Vue.set(
            this.extraPoints.national_monument,
            "total",
            parseFloat(this.extraPoints.national_monument.points + "")
          );
        } else {
          Vue.set(this.extraPoints.national_monument, "total", 0);
        }
        var total = sum;
        Vue.set(this.extraPoints, "total", total);
        this.countDependantTotal();
      },
      countDeductionPoints() {
        var sum = 0;
  
        for (var ob in this.deductionOptions) {
          if (ob != "total") {
            if (this.deductionOptions[ob].value && parseFloat(this.deductionOptions[ob].value + "")) {
              sum += parseFloat(this.deductionOptions[ob].points + "");
              Vue.set(
                this.deductionOptions[ob],
                "total",
                parseFloat(this.deductionOptions[ob].points + "")
              );
            } else {
              Vue.set(this.deductionOptions[ob], "total", 0);
            }
          }
        }
        var max =
          (this.leaveArea.total +
            this.heating.total +
            this.kitchenFacilities.total +
            this.extraPoints.total) /
          2;
          if (Math.abs(sum) >= Math.abs(max)) {
            sum = -max;
          }
          var total = sum;
          Vue.set(this.deductionOptions, "total", total);
          this.countDependantTotal();
        },
      countDependantTotal() {
          var total =
          parseFloat(this.leaveArea.total+"") +
          parseFloat(this.heating.total+"") +
          parseFloat(this.kitchenFacilities.total+"") +
          parseFloat(this.extraPoints.total+"") +
          parseFloat(this.deductionOptions.total+"");
        this.total = Math.round(total);
        this.totalPoints = this.total;
        var p = this.totalPoints < 750 ? this.totalPoints : 750;
        this.price = this.dependantMaxRentalPrices[p];
      },
      totalLabel(text) {
        let addText = this.locales && this.locales.common && this.locales.common.total;
        if (text && addText) {
          return `${text} ${addText}`;
        }
        if (text) {
          return text;
        }
        if (addText) {
          return addText;
        }
        return '---';
      },
    },
  };
</script>
